/**
 * Cognara AI surfaces for the NFA module — register sweep, single-note deep
 * analysis and the raise-wizard copilot. The engine is always presented as
 * "Cognara AI"; no model or vendor name is ever shown.
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, BellRing, ChevronDown, Loader2, Siren, Sparkles, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { money, stamp } from './nfaTheme';
import { Btn, Empty, Panel, Pill } from './NfaPrimitives';

const VERDICT_TONE = (T) => ({
  APPROVE: T.green, APPROVE_WITH_CONDITIONS: T.primary, HOLD: T.amber, REJECT: T.red,
});
const RISK_TONE = (T) => ({ CRITICAL: T.red, HIGH: T.accent, MEDIUM: T.amber, LOW: T.green });

const Block = ({ T, label, children, testId }) => (
  <div data-testid={testId}>
    <div className="text-[9px] uppercase tracking-[0.1em] mb-1" style={{ color: T.muted }}>{label}</div>
    <div className="text-[11.5px] leading-relaxed" style={{ color: T.body }}>{children}</div>
  </div>
);

const Bullets = ({ T, label, items, tone, testId, onPush }) => (!items?.length ? null : (
  <div data-testid={testId}>
    <div className="text-[9px] uppercase tracking-[0.1em] mb-1" style={{ color: T.muted }}>{label}</div>
    <ul className="space-y-1">
      {items.map((t, i) => {
        const text = typeof t === 'string' ? t : JSON.stringify(t);
        return (
          <li key={i} className="text-[11.5px] flex gap-2 leading-relaxed" style={{ color: T.body }}>
            <span style={{ color: tone || T.primary }}>▪</span>
            <span className="flex-1">{text}</span>
            {onPush && (
              <button type="button" onClick={() => onPush(text)}
                data-testid={testId ? `${testId}-raid-${i}` : undefined}
                className="shrink-0 text-[9px] px-1.5 py-0.5 rounded-full font-semibold self-start"
                style={{ background: T.tint(T.accent), color: T.accent }}>+ RAID</button>
            )}
          </li>
        );
      })}
    </ul>
  </div>
));

/** Starts a Cognara job and polls it so the UI can show real percentage. */
const useCognaraJob = () => {
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState('');
  const [running, setRunning] = useState(false);

  const start = useCallback(async (url, body) => {
    setRunning(true); setProgress(3); setPhase('Starting Cognara AI');
    try {
      const r = await api.post(url, body);
      const jobId = r.data?.job_id;
      if (!jobId) return r.data;                       // non-job response
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((res) => setTimeout(res, 1500));
        const j = await api.get(`/nfa/cognara/jobs/${jobId}`);
        setProgress(j.data.progress || 0);
        setPhase(j.data.phase || '');
        if (j.data.status === 'DONE') return j.data.result;
        if (j.data.status === 'ERROR') throw new Error(j.data.error || 'Cognara AI failed');
      }
    } finally { setRunning(false); setProgress(0); setPhase(''); }
  }, []);

  return { start, progress, phase, running };
};

const ProgressBar = ({ T, progress, phase, testId }) => (
  <div className="space-y-1" data-testid={testId}>
    <div className="flex items-center justify-between text-[10.5px]">
      <span style={{ color: T.primary }}>{phase || 'Working…'}</span>
      <span className="tabular-nums font-semibold" style={{ color: T.ink }}>{progress}%</span>
    </div>
    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: T.surfaceAlt }}>
      <div className="h-full rounded-full transition-all duration-700"
        style={{ width: `${Math.max(3, progress)}%`, background: T.primary }} />
    </div>
  </div>
);

const pushRaid = async (nfaId, title, section, category, priority) => {
  try {
    const r = await api.post('/nfa/assist/raid', {
      nfa_id: nfaId, title, description: title,
      category, raid_category: 'COMMERCIAL', priority, source_section: section,
    });
    toast.success(`${r.data.raidId} created in the RAID log`);
  } catch (e) {
    toast.error(e?.response?.data?.detail || 'Could not push this to RAID');
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// 1 · Single-note deep analysis (inside the detail drawer)
// ═══════════════════════════════════════════════════════════════════════════
export const NfaNoteAnalysis = ({ nfaId, T }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const job = useCognaraJob();

  useEffect(() => {
    api.get(`/nfa/cognara/nfa/${nfaId}`).then((r) => {
      if (r.data?.analysis) setData(r.data);
    }).catch(() => {});
  }, [nfaId]);

  const run = useCallback(async (force) => {
    try {
      const res = await job.start(`/nfa/cognara/nfa/${nfaId}${force ? '?force=true' : ''}`);
      setData(res);
      toast.success('Cognara analysis ready');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Cognara AI could not analyse this note');
    }
  }, [nfaId, job]);

  const escalate = async () => {
    try {
      const r = await api.post(`/nfa/assist/escalate/${nfaId}`, {
        severity: data?.analysis?.risk_level || 'HIGH',
        cognara_summary: data?.analysis?.headline,
        reason: data?.analysis?.next_best_action,
      });
      toast.success(`Escalated at level ${r.data.level_no} — ${r.data.notified.length} people notified via ${r.data.channel}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not escalate this note'); }
  };

  const nudge = async () => {
    try {
      const r = await api.post(`/nfa/assist/remind/${nfaId}`);
      toast.success(`Reminder sent to ${r.data.reminded.length} approver(s) at level ${r.data.level_no}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send the reminder'); }
  };

  const a = data?.analysis;
  const vt = VERDICT_TONE(T)[a?.verdict] || T.muted;
  const rt = RISK_TONE(T)[a?.risk_level] || T.muted;

  return (
    <Panel T={T} testId="nfa-cognara-note" title="Cognara AI analysis"
      subtitle="Ultra-detailed commercial, budget, schedule, contractual and compliance review"
      right={<>
        {data?.generatedAt && (
          <span className="text-[9.5px]" style={{ color: T.muted }}>
            saved · {stamp(data.generatedAt)}
          </span>
        )}
        <Btn T={T} variant="warn" testId="nfa-cognara-remind" onClick={nudge}>
          <BellRing className="w-3.5 h-3.5" /> Remind approver
        </Btn>
        <Btn T={T} variant="danger" testId="nfa-cognara-escalate" onClick={escalate}>
          <Siren className="w-3.5 h-3.5" /> Escalate
        </Btn>
        <Btn T={T} disabled={job.running} testId="nfa-cognara-note-run" onClick={() => run(!!a)}>
          {job.running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {a ? 'Re-analyse' : 'Analyse with Cognara'}
        </Btn>
      </>}>
      {job.running && (
        <div className="mb-3">
          <ProgressBar T={T} progress={job.progress} phase={job.phase} testId="nfa-cognara-note-progress" />
        </div>
      )}
      {!a ? (
        <Empty T={T} testId="nfa-cognara-note-empty">
          Cognara AI reads the note, every attachment, the resolved approval route, the audit trail
          and comparable notes on this project, then returns a decision-grade review.
        </Empty>
      ) : (
        <div className="space-y-3.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10.5px] px-2 py-0.5 rounded-full font-bold" data-testid="nfa-cognara-verdict"
              style={{ background: T.tint(vt), color: vt, border: `1px solid ${T.tint(vt, '44')}` }}>
              {String(a.verdict || '').replace(/_/g, ' ')}
            </span>
            <span className="text-[10.5px] px-2 py-0.5 rounded-full font-semibold" data-testid="nfa-cognara-risk"
              style={{ background: T.tint(rt), color: rt }}>{a.risk_level} RISK</span>
            {a.confidence != null && (
              <span className="text-[10px]" style={{ color: T.muted }}>
                confidence {(Number(a.confidence) * 100).toFixed(0)}%
              </span>
            )}
            <span className="text-[10px] ml-auto" style={{ color: T.muted }}>
              {data.attachmentsSeen} attachments · {data.precedentSeen} precedent notes read
            </span>
          </div>

          <p className="text-[12.5px] font-semibold leading-snug" style={{ color: T.ink }}
            data-testid="nfa-cognara-headline">{a.headline}</p>
          <p className="text-[11.5px] leading-relaxed" style={{ color: T.body }}>{a.executive_summary}</p>

          <button type="button" onClick={() => setOpen((v) => !v)} data-testid="nfa-cognara-toggle"
            className="inline-flex items-center gap-1 text-[10.5px] font-semibold"
            style={{ color: T.primary }}>
            <ChevronDown className="w-3.5 h-3.5" style={{ transform: open ? 'none' : 'rotate(-90deg)' }} />
            {open ? 'Hide the full review' : 'Show the full review'}
          </button>

          {open && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              <Block T={T} label="Commercial" testId="nfa-cognara-commercial">{a.commercial_analysis}</Block>
              <Block T={T} label="Budget impact" testId="nfa-cognara-budget">{a.budget_impact}</Block>
              <Block T={T} label="Schedule impact" testId="nfa-cognara-schedule">{a.schedule_impact}</Block>
              <Block T={T} label="Contractual risk" testId="nfa-cognara-contract">{a.contractual_risk}</Block>
              <Block T={T} label="Compliance & delegation" testId="nfa-cognara-compliance">{a.compliance_check}</Block>
              <Block T={T} label="Precedent" testId="nfa-cognara-precedent">{a.precedent_view}</Block>
              <Block T={T} label="Route sanity" testId="nfa-cognara-route">{a.route_sanity}</Block>
              <Block T={T} label="SLA view" testId="nfa-cognara-sla">{a.sla_view}</Block>
              {a.financial_view && (
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-2">
                  {[['Requested', a.financial_view.requested],
                    ['Benchmark', a.financial_view.benchmark_comment],
                    ['Cash timing', a.financial_view.cash_timing]].map(([l, v]) => (
                    <div key={l} className="rounded-md px-2.5 py-2"
                      style={{ background: T.surfaceAlt, border: `1px solid ${T.border}` }}>
                      <div className="text-[9px] uppercase tracking-[0.1em]" style={{ color: T.muted }}>{l}</div>
                      <div className="text-[11px] mt-0.5" style={{ color: T.ink }}>{v}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3.5">
                <Bullets T={T} label="Red flags" items={a.red_flags} tone={T.red} testId="nfa-cognara-flags"
                  onPush={(t) => pushRaid(nfaId, t, 'red_flag', 'RISK', 'HIGH')} />
                <Bullets T={T} label="Missing evidence" items={a.missing_evidence} tone={T.accent}
                  testId="nfa-cognara-missing"
                  onPush={(t) => pushRaid(nfaId, t, 'missing_evidence', 'ACTION', 'MEDIUM')} />
                <Bullets T={T} label="Questions for the originator" items={a.questions_for_originator}
                  testId="nfa-cognara-questions"
                  onPush={(t) => pushRaid(nfaId, t, 'question', 'ACTION', 'MEDIUM')} />
                <Bullets T={T} label="Conditions to attach" items={a.conditions} tone={T.primary}
                  testId="nfa-cognara-conditions"
                  onPush={(t) => pushRaid(nfaId, t, 'condition', 'DECISION', 'MEDIUM')} />
                <Bullets T={T} label="Negotiation levers" items={a.negotiation_levers} tone={T.green}
                  testId="nfa-cognara-levers"
                  onPush={(t) => pushRaid(nfaId, t, 'lever', 'ACTION', 'LOW')} />
              </div>
              <div className="md:col-span-2 rounded-md px-3 py-2.5"
                style={{ background: T.tint(T.primary), border: `1px solid ${T.tint(T.primary, '33')}` }}
                data-testid="nfa-cognara-next">
                <div className="text-[9px] uppercase tracking-[0.1em]" style={{ color: T.primary }}>
                  Next best action
                </div>
                <div className="text-[12px] font-semibold mt-0.5" style={{ color: T.ink }}>
                  {a.next_best_action}
                </div>
              </div>
            </div>
          )}
          {a.degraded && (
            <p className="text-[10.5px] flex items-center gap-1.5" style={{ color: T.amber }}>
              <AlertTriangle className="w-3.5 h-3.5" /> Deterministic screen only — the AI analyst was
              unreachable when this ran.
            </p>
          )}
        </div>
      )}
    </Panel>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// 2 · Register-level sweep
// ═══════════════════════════════════════════════════════════════════════════
const HEALTH_TONE = (T) => ({ CRITICAL: T.red, STRAINED: T.amber, HEALTHY: T.green });

export const NfaRegisterSweep = ({ projectId, projectName, T, onOpenNfa }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [escalating, setEscalating] = useState(null);
  const job = useCognaraJob();

  useEffect(() => {
    if (!projectId) return;
    setData(null); setOpen(false);
    api.get(`/nfa/cognara/sweep/latest?project_id=${projectId}`)
      .then((r) => { if (r.data?.sweep) setData(r.data); })
      .catch(() => {});
  }, [projectId]);

  const run = useCallback(async () => {
    try {
      const res = await job.start('/nfa/cognara/sweep', { project_id: projectId, force: true });
      setData(res); setOpen(true);
      toast.success('Cognara pipeline brief ready');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Cognara sweep failed');
    }
  }, [projectId, job]);

  const escalate = async (n) => {
    setEscalating(n.nfa_no);
    try {
      const row = (data?.rowsByNo || {})[n.nfa_no];
      const nfaId = row || await api.get(`/nfa?project_id=${projectId}&q=${encodeURIComponent(n.nfa_no)}&limit=1`)
        .then((r) => r.data.nfas?.[0]?.nfa_id);
      if (!nfaId) { toast.error(`Could not resolve ${n.nfa_no}`); return; }
      const r = await api.post(`/nfa/assist/escalate/${nfaId}`, {
        severity: n.severity, cognara_summary: n.why, reason: n.action });
      toast.success(`${n.nfa_no} escalated at level ${r.data.level_no} — ${r.data.notified.length} notified (${r.data.channel})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not escalate that note');
    } finally { setEscalating(null); }
  };

  const s = data?.sweep;
  const ht = HEALTH_TONE(T)[s?.pipeline_health] || T.muted;

  return (
    <Panel T={T} testId="nfa-cognara-sweep" title="Cognara AI — approval pipeline brief"
      subtitle={`Director-level read of every note on ${projectName || projectId}: where approvals stall and what it costs`}
      right={<>
        {data?.generatedAt && (
          <span className="text-[9.5px]" style={{ color: T.muted }}>saved · {stamp(data.generatedAt)}</span>
        )}
        <Btn T={T} disabled={job.running} testId="nfa-cognara-sweep-run" onClick={run}>
          {job.running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {s ? 'Re-sweep register' : 'Sweep the register'}
        </Btn>
        <button type="button" onClick={() => setOpen((v) => !v)} data-testid="nfa-sweep-collapse"
          className="p-1 rounded" title={open ? 'Collapse' : 'Expand'}
          style={{ background: T.tint(T.primary), color: T.primary }}>
          <ChevronDown className="w-3.5 h-3.5"
            style={{ transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform 150ms' }} />
        </button>
      </>}>
      {job.running && (
        <div className="mb-3">
          <ProgressBar T={T} progress={job.progress} phase={job.phase} testId="nfa-cognara-sweep-progress" />
        </div>
      )}
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} data-testid="nfa-sweep-peek"
          className="w-full text-left text-[11.5px] flex items-center gap-2 flex-wrap">
          {s ? (
            <>
              <span className="px-2 py-0.5 rounded-full font-bold text-[10.5px]"
                style={{ background: T.tint(ht), color: ht }}>{s.pipeline_health}</span>
              <span className="truncate flex-1" style={{ color: T.ink }}>{s.headline}</span>
              <span className="text-[10px]" style={{ color: T.primary }}>show brief</span>
            </>
          ) : (
            <span style={{ color: T.muted }}>
              No sweep yet — one click reads every note on this project and ranks the riskiest. Click to expand.
            </span>
          )}
        </button>
      ) : !s ? (
        <Empty T={T} testId="nfa-cognara-sweep-empty">
          One click reads every note on this project — value at risk, the riskiest notes, the level that
          is blocking the pipeline and which SLAs breach next.
        </Empty>
      ) : (
        <div className="space-y-3.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10.5px] px-2 py-0.5 rounded-full font-bold" data-testid="nfa-sweep-health"
              style={{ background: T.tint(ht), color: ht }}>{s.pipeline_health}</span>
            <span className="text-[10.5px]" style={{ color: T.muted }}>
              {data.stats?.total} notes · {data.stats?.overdue} overdue · open value{' '}
              <strong style={{ color: T.ink }}>{money(data.stats?.openValue)}</strong>
            </span>
            <button type="button" onClick={() => setOpen((v) => !v)} className="ml-auto text-[10.5px] font-semibold"
              style={{ color: T.primary }} data-testid="nfa-sweep-toggle">
              {open ? 'Collapse' : 'Expand'}
            </button>
          </div>
          <p className="text-[12.5px] font-semibold leading-snug" style={{ color: T.ink }}
            data-testid="nfa-sweep-headline">{s.headline}</p>
          {open && (
            <>
              <p className="text-[11.5px] leading-relaxed" style={{ color: T.body }}>{s.executive_brief}</p>
              <Block T={T} label="Value at risk">{s.value_at_risk_comment}</Block>
              {!!s.riskiest_notes?.length && (
                <div data-testid="nfa-sweep-riskiest">
                  <div className="text-[9px] uppercase tracking-[0.1em] mb-1" style={{ color: T.muted }}>
                    Riskiest notes
                  </div>
                  <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${T.border}` }}>
                    {s.riskiest_notes.map((n) => (
                      <div key={n.nfa_no} className="px-3 py-2 flex items-start gap-2.5"
                        data-testid={`nfa-sweep-risk-${n.nfa_no}`}
                        style={{ borderTop: `1px solid ${T.border}` }}>
                        <Pill value={n.severity} T={T} />
                        <button type="button" onClick={() => onOpenNfa?.(n.nfa_no)}
                          data-testid={`nfa-sweep-open-${n.nfa_no}`}
                          className="font-mono text-[10.5px] shrink-0 tabular-nums hover:underline"
                          style={{ color: T.primary }}>{n.nfa_no}</button>
                        <span className="text-[11px] flex-1" style={{ color: T.body }}>
                          {n.why}
                          <span className="block mt-0.5" style={{ color: T.ink }}>
                            → {n.action} <em style={{ color: T.muted }}>({n.owner_role})</em>
                          </span>
                        </span>
                        <button type="button" onClick={() => escalate(n)} disabled={escalating === n.nfa_no}
                          data-testid={`nfa-sweep-escalate-${n.nfa_no}`}
                          className="shrink-0 inline-flex items-center gap-1 text-[9.5px] px-2 py-0.5 rounded-full font-semibold disabled:opacity-50"
                          style={{ background: T.tint(T.red), color: T.red }}>
                          <Siren className="w-3 h-3" />
                          {escalating === n.nfa_no ? 'Escalating…' : 'Escalate'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {!!s.bottlenecks?.length && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="nfa-sweep-bottlenecks">
                  {s.bottlenecks.map((b) => (
                    <div key={`${b.level_no}-${b.role}`} className="rounded-md px-2.5 py-2"
                      style={{ background: T.surfaceAlt, border: `1px solid ${T.border}` }}>
                      <div className="text-[10.5px] font-semibold" style={{ color: T.ink }}>
                        Level {b.level_no} · {b.role}
                      </div>
                      <p className="text-[11px] mt-0.5" style={{ color: T.body }}>{b.diagnosis}</p>
                      <p className="text-[11px] mt-1" style={{ color: T.primary }}>Fix: {b.fix}</p>
                    </div>
                  ))}
                </div>
              )}
              <Block T={T} label="SLA forecast">{s.sla_forecast}</Block>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                <Bullets T={T} label="Themes" items={s.themes} />
                <Bullets T={T} label="Policy observations" items={s.policy_observations} tone={T.accent} />
              </div>
              {!!s.recommendations?.length && (
                <div data-testid="nfa-sweep-recommendations">
                  <div className="text-[9px] uppercase tracking-[0.1em] mb-1" style={{ color: T.muted }}>
                    Recommendations
                  </div>
                  <div className="space-y-1.5">
                    {s.recommendations.map((r, i) => (
                      <div key={i} className="flex items-start gap-2 text-[11.5px]">
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full shrink-0 font-bold"
                          style={{ background: T.tint(r.urgency === 'NOW' ? T.red : T.primary),
                                   color: r.urgency === 'NOW' ? T.red : T.primary }}>
                          {String(r.urgency || '').replace(/_/g, ' ')}
                        </span>
                        <span style={{ color: T.body }}>
                          <strong style={{ color: T.ink }}>{r.action}</strong> — {r.impact}
                          <em style={{ color: T.muted }}> ({r.owner_role})</em>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Panel>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// 3 · Raise-wizard copilot
// ═══════════════════════════════════════════════════════════════════════════
export const CognaraDraftAssist = ({ T, draft, onApply, attachmentNames }) => {
  const [out, setOut] = useState(null);
  const job = useCognaraJob();

  const run = async () => {
    try {
      const res = await job.start('/nfa/cognara/draft', {
        project_id: draft.project_id, nfa_type: draft.nfa_type, title: draft.title,
        wbs_element: draft.wbs_element, requested_amount: Number(draft.requested_amount || 0) || null,
        currency: draft.currency, vendor_name: draft.vendor_name,
        justification: draft.justification, business_case: draft.business_case,
        required_by_date: draft.required_by_date, attachment_names: attachmentNames || [],
      });
      setOut(res.draft || res);
      toast.success('Cognara draft ready — review and apply');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Cognara AI could not draft this note');
    }
  };

  return (
    <div className="rounded-lg p-3 space-y-2.5" data-testid="nfa-cognara-draft"
      style={{ background: T.tint(T.primary), border: `1px dashed ${T.tint(T.primary, '44')}` }}>
      <div className="flex items-center gap-2 flex-wrap">
        <Wand2 className="w-3.5 h-3.5" style={{ color: T.primary }} />
        <span className="text-[11.5px] font-semibold" style={{ color: T.ink }}>Cognara AI copilot</span>
        <span className="text-[10px]" style={{ color: T.muted }}>
          Drafts the title, justification and business case, picks the WBS element and pre-flights the note
        </span>
        <Btn T={T} disabled={job.running} testId="nfa-cognara-draft-run" onClick={run}>
          {job.running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {out ? 'Draft again' : 'Draft with Cognara'}
        </Btn>
      </div>
      {job.running && (
        <ProgressBar T={T} progress={job.progress} phase={job.phase} testId="nfa-cognara-draft-progress" />
      )}
      {out && (
        <div className="space-y-2.5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {[['Suggested title', out.title], ['Type', out.nfa_type],
              ['WBS element', `${out.wbs_element} — ${out.wbs_reason || ''}`]].map(([l, v]) => (
              <div key={l} className="rounded-md px-2.5 py-2"
                style={{ background: T.surface, border: `1px solid ${T.border}` }}>
                <div className="text-[9px] uppercase tracking-[0.1em]" style={{ color: T.muted }}>{l}</div>
                <div className="text-[11px] mt-0.5" style={{ color: T.ink }}>{v}</div>
              </div>
            ))}
          </div>
          <Block T={T} label="Justification draft" testId="nfa-cognara-draft-justification">
            {out.justification}
          </Block>
          <Block T={T} label="Business case draft">{out.business_case}</Block>
          <Block T={T} label="Amount sanity">{out.amount_sanity} {out.suggested_amount_note}</Block>
          <Block T={T} label="Required-by advice">{out.required_by_advice}</Block>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Bullets T={T} label="Evidence checklist" items={out.evidence_checklist} />
            <Bullets T={T} label="Still missing" items={out.missing_now} tone={T.accent} />
            <Bullets T={T} label="Risk flags" items={out.risk_flags} tone={T.red} />
            <Bullets T={T} label="Approver tips" items={out.approver_tips} tone={T.green} />
          </div>
          <Btn T={T} testId="nfa-cognara-draft-apply" onClick={() => {
            onApply({
              title: out.title || draft.title,
              nfa_type: out.nfa_type || draft.nfa_type,
              wbs_element: out.wbs_element || draft.wbs_element,
              justification: out.justification || draft.justification,
              business_case: out.business_case || draft.business_case,
            });
            toast.success('Cognara draft applied to the form');
          }}>Apply this draft to the form</Btn>
        </div>
      )}
    </div>
  );
};

export default NfaNoteAnalysis;
